"use client";

import React from "react";
import { FileCheck2, Cpu, RefreshCw, ShieldCheck, ArrowRight, ClipboardList, CircleCheck as CheckCircle2, FileSpreadsheet } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface ValidationItem {
  id: number;
  title: string;
  category: string;
  description: string;
  standard: string;
}
const validationItems: ValidationItem[] = [{
  id: 1,
  title: "IQ/OQ Protocols",
  category: "Hardware & Installation",
  description: "Installation and operational qualification protocols ensuring analytical equipment meets vendor and laboratory specifications.",
  standard: "GAMP 5 / USP <1058>"
}, {
  id: 2,
  title: "Chemometric Model Validation",
  category: "Algorithm & Spectroscopy",
  description: "Independent validation of multivariate calibration models, quantitative algorithms, and spectral classification routines.",
  standard: "ICH Q2(R2) / ASTM E1655"
}, {
  id: 3,
  title: "Ongoing Model Lifecycle Management",
  category: "Routine Verification",
  description: "Continuous performance monitoring, routine slope/intercept diagnostics, drift correction, and scheduled re-calibrations.",
  standard: "EU GMP Annex 15"
}, {
  id: 4,
  title: "Computer System Validation (CSV)",
  category: "Data Integrity",
  description: "Software qualification, audit trail verification, user access control, and electronic records compliance verification.",
  standard: "Data Integrity Verification"
}, {
  id: 5,
  title: "Analytical Method Transfer (AMT)",
  category: "Methodology",
  description: "End-to-end comparative testing protocols and acceptance criteria definition between R&D, QC labs, and production sites.",
  standard: "WHO / ICH Q14"
}, {
  id: 6,
  title: "Periodic Review & Re-Qualification",
  category: "Quality Governance",
  description: "Risk-based periodic assessments, post-maintenance qualification checks, and formal change control documentation.",
  standard: "ISO/IEC 17025 / cGMP"
}];
const iconsMap = [FileCheck2, Cpu, RefreshCw, ShieldCheck, ClipboardList, FileSpreadsheet];
export default function QualificationProtocols() {
  const shouldReduceMotion = useReducedMotion();
  const motionConfig = {
    initial: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5
    }
  };
  return <section id="qualificationprotocols" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left Column (6 of 12) */}
          <motion.div className="lg:col-span-5 flex flex-col justify-between space-y-6 lg:sticky lg:top-28" initial={motionConfig.initial} whileInView={motionConfig.animate} viewport={{
          once: true
        }} transition={motionConfig.transition}>
            <div className="space-y-4">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                Compliance & Quality Assurance
              </Badge>

              <h3 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground tracking-tight leading-tight">
                Qualification Protocols
              </h3>

              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed pt-2">
                Application-led analytical technology, qualification, and lifecycle support tailored for regulated pharmaceutical, bioprocess, and manufacturing teams across Ireland and the UK.
              </p>
            </div>

            <div className="bg-secondary/40 border border-border rounded-xl p-5 sm:p-6 space-y-4">
              <h4 className="font-default text-base font-semibold text-foreground">
                Documented Regulatory Rigour
              </h4>
              <ul className="space-y-2.5 text-sm text-muted-foreground">
                <li className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Pre-written and execution-ready protocol templates</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Full traceability to international standards</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Direct support during customer audit reviews</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button asChild size="lg" className="font-medium shadow-sm">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  Request Protocol Scope
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-medium">
                <Link to="/ServicesPage">
                  View All Services
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column (6 of 12): List of 6 Validation Items */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
            {validationItems.map((item, index) => {
            const IconComponent = iconsMap[index % iconsMap.length];
            return <div key={item.id} data-index={index}>
                  <motion.div initial={motionConfig.initial} whileInView={motionConfig.animate} viewport={{
                once: true
              }} transition={{
                duration: 0.45,
                delay: shouldReduceMotion ? 0 : index * 0.08
              }}>
                    <Card className="border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-[border-color,box-shadow] duration-200">
                      <CardContent className="p-5 sm:p-6 flex flex-col gap-3">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-lg bg-secondary text-primary shrink-0 border border-border/60">
                              <IconComponent className="size-5" />
                            </div>
                            <div>
                              <span className="text-xs font-medium text-muted-foreground block">
                                {item.category}
                              </span>
                              <h4 className="font-default text-lg font-semibold text-foreground leading-snug">
                                {item.title}
                              </h4>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-[11px] font-mono shrink-0 hidden sm:inline-flex bg-background">
                            {item.standard}
                          </Badge>
                        </div>

                        <p className="text-sm text-muted-foreground leading-relaxed pl-0 sm:pl-11">
                          {item.description}
                        </p>

                        <div className="sm:hidden pt-1 pl-0">
                          <span className="text-[11px] font-mono text-muted-foreground bg-secondary px-2 py-0.5 rounded border border-border">
                            {item.standard}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}